import { useMemo, useState } from "react"
import { Ship } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"

import { LoadingState } from "@/components/loading-state"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { LotDetail } from "@/features/lots/api/types"
import { lotKeys } from "@/features/lots/hooks/use-lots"
import { StageChip } from "@/features/transit/components/stage-chip"
import { useAttachLots } from "@/features/transit/hooks/use-lot-mutations"
import type { OTListItem } from "@/features/transit/api/types"
import { useTransitOrders } from "@/features/transit/hooks/use-transit-orders"

interface AssignToOTDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lot: LotDetail
}

// Picker for attaching this lot to an existing in-flight OT. Only OTs
// that haven't shipped (stage != TERMINER) and aren't archived are
// eligible — backend mirrors this guard in transit.AttachLots.
export function AssignToOTDialog({
  open,
  onOpenChange,
  lot,
}: AssignToOTDialogProps) {
  const { t } = useTranslation("lots")
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const { data, isLoading } = useTransitOrders({ tab: "in_progress" })
  const candidates = useMemo<OTListItem[]>(
    () => (data ?? []).filter((ot) => !ot.archived),
    [data],
  )

  const selected = candidates.find((c) => c.id === selectedId) ?? null
  const attach = useAttachLots(selectedId ?? 0)

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) setSelectedId(null)
  }

  const submit = async () => {
    if (!selected) return
    await attach.mutateAsync({ lotIds: [lot.id] })
    await queryClient.invalidateQueries({ queryKey: lotKeys.details(lot.id) })
    handleOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("detail.assignOT.title")}</DialogTitle>
          <DialogDescription>
            {t("detail.assignOT.description", { lot: lot.lotNumber })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoadingState />
        ) : candidates.length === 0 ? (
          <Card className="border-dashed p-6 text-center">
            <Ship className="text-muted-foreground mx-auto mb-2 size-5" />
            <p className="text-muted-foreground text-sm">
              {t("detail.assignOT.empty")}
            </p>
          </Card>
        ) : (
          <Card className="max-h-80 overflow-y-auto p-0">
            <ul className="divide-y">
              {candidates.map((ot) => (
                <OTRow
                  key={ot.id}
                  ot={ot}
                  selected={ot.id === selectedId}
                  onSelect={() => setSelectedId(ot.id)}
                />
              ))}
            </ul>
          </Card>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={attach.isPending}
          >
            {t("detail.assignOT.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!selected || attach.isPending}
          >
            {t("detail.assignOT.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OTRowProps {
  ot: OTListItem
  selected: boolean
  onSelect: () => void
}

function OTRow({ ot, selected, onSelect }: OTRowProps) {
  const { t } = useTranslation("lots")

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
          selected ? "bg-primary/5" : "hover:bg-muted/40"
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-sm font-medium">{ot.otNumber}</span>
            {ot.contractNumber ? (
              <span className="text-muted-foreground font-mono text-[10px]">
                · {ot.contractNumber}
              </span>
            ) : null}
          </div>
          <div className="text-muted-foreground mt-0.5 truncate text-xs">
            {ot.clientName || t("detail.assignOT.noClient")}
          </div>
        </div>
        <div className="text-muted-foreground tabular-nums text-[11px]">
          {t("detail.assignOT.lotCount", { count: ot.lotCount })}
        </div>
        <StageChip stage={ot.status} archived={ot.archived} />
      </button>
    </li>
  )
}
